import { contractNameOverride } from '@/domain/common/format'
import { queryOptions } from '@tanstack/react-query'
import { Address } from 'viem'
import { Config } from 'wagmi'
import { CheckedAddress } from '../../types/CheckedAddress'
import { Token } from '../../types/Token'
import { getBalancesQueryKeyPrefix } from '../getBalancesQueryKeyPrefix'
import { TokensInfo } from './TokenInfo'
import { createAssetDataFetcher } from './createAssetDataFetcher'
import { createOraclePriceFetcher } from './createOraclePriceFetcher'
import type { TokenConfig } from './types'

interface TokensParams {
  tokens: TokenConfig[]
  wagmiConfig: Config
  chainId: number
  account?: Address
}

function normalizeTokenConfig(token: TokenConfig): TokenConfig {
  return {
    ...token,
    address: CheckedAddress(token.address.toLowerCase()),
    oracleAddress: token.oracleAddress ? CheckedAddress(token.oracleAddress.toLowerCase()) : undefined,
  }
}

function tokenConfigKey(token: TokenConfig): string {
  return [token.address.toLowerCase(), token.oracleType, token.oracleAddress?.toLowerCase() ?? ''].join(':')
}

function normalizeTokenConfigs(tokens: TokenConfig[]): TokenConfig[] {
  const byKey = new Map<string, TokenConfig>()

  for (const token of tokens) {
    const normalized = normalizeTokenConfig(token)
    byKey.set(tokenConfigKey(normalized), normalized)
  }

  return Array.from(byKey.values()).sort((a, b) => tokenConfigKey(a).localeCompare(tokenConfigKey(b)))
}

export function tokensQueryOptions({ tokens, wagmiConfig, chainId, account }: TokensParams) {
  const normalizedTokens = normalizeTokenConfigs(tokens)

  return queryOptions<TokensInfo>({
    queryKey: normalizedTokensQueryKey({ tokens: normalizedTokens, account, chainId }),
    queryFn: async () => {
      const tokensWithBalances = await Promise.all(
        normalizedTokens.map(async (tokenConfig) => {
          const getOraclePrice = createOraclePriceFetcher({ tokenConfig, wagmiConfig, chainId })
          const getAssetData = createAssetDataFetcher({ tokenConfig, wagmiConfig, chainId, account })

          const [assetData, oraclePrice] = await Promise.all([getAssetData(), getOraclePrice()])

          const token = new Token({
            name: contractNameOverride(assetData.name),
            decimals: assetData.decimals,
            address: tokenConfig.address,
            symbol: assetData.symbol,
            unitPriceUsd: oraclePrice.toFixed(),
          })

          return { token, balance: assetData.balance }
        }),
      )

      return new TokensInfo(tokensWithBalances)
    },
    enabled: !!normalizedTokens.length,
  })
}

function normalizedTokensQueryKey({ tokens, account, chainId }: Omit<TokensParams, 'wagmiConfig'>): unknown[] {
  return [...getBalancesQueryKeyPrefix({ account, chainId }), 'tokens', tokens]
}

export function tokensQueryKey({ tokens, account, chainId }: Omit<TokensParams, 'wagmiConfig'>): unknown[] {
  return normalizedTokensQueryKey({ tokens: normalizeTokenConfigs(tokens), account, chainId })
}
